if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.removeLegacyB2BPills();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        const initialVariant = this.getSelectedVariant(this);
        if (initialVariant) {
          this.updateVariantInputs(initialVariant.id);
          this.updateTrackedSoldOutProductDetails(initialVariant);
        }
        this.productForm?.renderPdpPricing?.(initialVariant || null);
        this.syncInitialVariantState(initialVariant);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.handleCartUpdate.bind(this)
          );
        }
      }

      handleCartUpdate(event) {
        const currentVariantId = this.productForm?.variantIdInput?.value;

        if (currentVariantId && event?.cartData?.items?.length) {
          const matchingVariant = event.cartData.items.find(
            ({ variant_id: variantId }) => `${variantId}` === currentVariantId
          );

          this.quantityInput?.setAttribute(
            'data-cart-quantity',
            matchingVariant?.quantity ?? 0
          );
        }

        this.fetchQuantityRules();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        this.suppressPendingPriceDecorations();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const selectedVariantId = shouldSwapProduct ? null : this.getSelectedVariant(this)?.id;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            selectedVariantId,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      suppressPendingPriceDecorations() {
        const priceContainer = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceContainer) return;

        priceContainer.querySelectorAll('[data-b2b-price-pill-visibility]').forEach((pill) => {
          if (!(pill instanceof HTMLElement) || pill.hidden) return;

          pill.dataset.b2bPricePillPending = 'true';
          pill.setAttribute('aria-hidden', 'true');
        });

        priceContainer.querySelectorAll('[data-pdp-b2b-rrp]').forEach((row) => {
          if (!(row instanceof HTMLElement) || row.hidden) return;

          row.dataset.pdpB2bRrpPending = 'true';
          row.setAttribute('aria-hidden', 'true');
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            if (targetId) {
              document.querySelector(`#${targetId}`)?.focus();
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);
            }
          });
      }

      parseJsonScript(productInfoNode, selector) {
        const json = productInfoNode?.querySelector(selector)?.textContent?.trim();
        if (!json) return null;

        try {
          return JSON.parse(json);
        } catch (error) {
          console.error(`Failed to parse ${selector}`, error);
          return null;
        }
      }

      getVariantList(productInfoNode) {
        const variants = this.parseJsonScript(productInfoNode, 'variant-selects [data-variant-list]');
        return Array.isArray(variants) ? variants.map((variant) => this.withVariantInventory(productInfoNode, variant)) : [];
      }

      getVariantInventoryList(productInfoNode) {
        const variantInventoryList = this.parseJsonScript(productInfoNode, 'variant-selects [data-variant-inventory-list]');
        if (Array.isArray(variantInventoryList)) return variantInventoryList;

        const selectedVariantInventory = this.parseJsonScript(productInfoNode, '[data-product-form-selected-variant-inventory]');
        return selectedVariantInventory ? [selectedVariantInventory] : [];
      }

      getVariantInventoryById(productInfoNode, variantId) {
        if (!variantId) return null;

        return (
          this.getVariantInventoryList(productInfoNode).find((variantInventory) => `${variantInventory?.id}` === `${variantId}`) ||
          null
        );
      }

      withVariantInventory(productInfoNode, variant) {
        if (!variant) return null;

        const variantInventory = this.getVariantInventoryById(productInfoNode, variant.id);
        return variantInventory ? { ...variant, ...variantInventory } : variant;
      }

      getSelectedOptionValues(productInfoNode) {
        const variantSelects = productInfoNode?.querySelector('variant-selects');
        if (!variantSelects) return [];

        return Array.from(
          variantSelects.querySelectorAll('select, fieldset input:checked')
        ).map((input) => input.value?.trim());
      }

      findVariantBySelectedOptions(productInfoNode) {
        const selectedOptions = this.getSelectedOptionValues(productInfoNode);
        if (!selectedOptions.length) return null;

        return (
          this.getVariantList(productInfoNode).find((variant) => {
            const variantOptions = Array.isArray(variant.options)
              ? variant.options
              : [variant.option1, variant.option2, variant.option3].filter((value) => value !== null && value !== undefined);

            if (variantOptions.length !== selectedOptions.length) return false;

            return variantOptions.every((value, index) => value === selectedOptions[index]);
          }) || null
        );
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = this.parseJsonScript(productInfoNode, 'variant-selects [data-selected-variant]');
        const matchedVariant = this.findVariantBySelectedOptions(productInfoNode);
        const fallbackVariant = this.parseJsonScript(productInfoNode, '[data-product-form-selected-variant]');

        if (!selectedVariant) return this.withVariantInventory(productInfoNode, matchedVariant || fallbackVariant);
        if (!matchedVariant) return this.withVariantInventory(productInfoNode, selectedVariant);

        return this.withVariantInventory(productInfoNode, selectedVariant.id === matchedVariant.id ? selectedVariant : matchedVariant);
      }

      getServerRenderedVariant(productInfoNode) {
        return this.withVariantInventory(
          productInfoNode,
          this.parseJsonScript(productInfoNode, 'variant-selects [data-selected-variant]') ||
            this.parseJsonScript(productInfoNode, '[data-product-form-selected-variant]')
        );
      }

      syncInitialVariantState(initialVariant) {
        if (!initialVariant) return;

        const serverRenderedVariant = this.getServerRenderedVariant(this);
        if (!serverRenderedVariant?.id || serverRenderedVariant.id === initialVariant.id) return;

        const selectedOptionValues = this.getSelectedOptionValues(this);
        if (!selectedOptionValues.length) return;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(this.dataset.url, selectedOptionValues, initialVariant.id, false),
          callback: this.handleUpdateProductInfo(this.dataset.url),
        });
      }

      buildRequestUrlWithParams(url, optionValues, variantId = null, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (variantId) params.push(`variant=${variantId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.publishVariantChange(html, null);
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          this.updateProductInfoBlocks(html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
          this.removeLegacyB2BPills();
          this.updateTrackedSoldOutProductDetails(variant);

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          this.publishVariantChange(html, variant);
        };
      }

      updateProductInfoBlocks(html) {
        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
        updateSourceFromDestination('Volume');
        updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('BundledVariants', ({ classList }) => classList.contains('hidden'));
      }

      publishVariantChange(html, variant) {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant,
          },
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      getProductVendor() {
        return this.productForm?.dataset?.productVendor?.trim() || '';
      }

      getFabricsVendorName() {
        return this.productForm?.dataset?.fabricsVendorName?.trim() || '';
      }

      shouldHideTrackedSoldOutProductDetails(variant) {
        if (!variant) return false;
        if (!document.body.classList.contains('template-product')) return false;
        if (this.getProductVendor() === this.getFabricsVendorName()) return false;
        if (variant.inventory_management !== 'shopify') return false;
        if (variant.inventory_policy === 'continue') return false;

        const inventoryQuantity = Number.parseInt(variant.inventory_quantity, 10);
        const hasNoRemainingInventory = Number.isNaN(inventoryQuantity)
          ? variant.available === false
          : inventoryQuantity <= 0;

        return variant.available === false && hasNoRemainingInventory;
      }

      updateTrackedSoldOutProductDetails(variant) {
        if (!variant) return;

        const shouldHide = this.shouldHideTrackedSoldOutProductDetails(variant);
        const managedElements = [
          this.querySelector('[data-product-quantity-wrapper]'),
          this.querySelector('[data-product-subtotal]'),
          this.querySelector('[data-product-sold-out-badge]'),
        ].filter(Boolean);

        managedElements.forEach((element) => {
          element?.classList.toggle('hidden', shouldHide);
        });
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : Math.max(0, data.max - data.cartQuantity);
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max !== null) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        if (!this.quantityInput.value) {
          this.quantityInput.value = min;
        }
        if (max !== null && parseInt(this.quantityInput.value, 10) > max) {
          this.quantityInput.value = max;
        }

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        this.setQuantityBoundries();
      }

      removeLegacyB2BPills() {
        this.querySelectorAll('.price__b2b-pill').forEach((pill) => pill.remove());
      }

      get productForm() {
        return (
          this.querySelector('product-form[data-product-form-role="primary"]') ||
          this.querySelector(`product-form`)
        );
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
